import React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import PulseLoader from 'react-spinners/PulseLoader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'app/store';
import InvitationLinksActionsList from 'components/members/InvitationLinksActionsList';
import ActionsPopover from 'components/shared/ActionsPopover';
import { getInvitationsInfiniteScroll } from 'features/invitationLinksList/invitationLinksListActions';
import {
  IInvitation,
  InvitationsTableHeadings,
} from 'features/invitationLinksList/invitationLinksListTypes';
import {
  filterInvitationsSelector,
  isLoadingInvitationsSelector,
  itemsTotalCountInvitationsSelector,
  listInvitationsSelector,
} from 'features/invitationLinksList/invitationLinksListSlice';
import styles from 'components/shared/tables/infiniteScrollTable.module.scss';
import invitationsTableStyles from './InvitationLinksTable.module.scss'
import { dateToTimeZone } from 'helpers/dateToTimeZone';
import { format } from 'date-fns';
import { selectInfoUserSelector } from 'features/user/userSlice';

const invitationLinksTableColumns: Record<'name', InvitationsTableHeadings>[] = [
  { name: 'Role' },
  { name: 'Expiration date' },
  { name: 'User name' },
  { name: 'Email' },
  { name: 'Actions' },
];

const InvitationLinksTable: React.FC = () => {
  const invitationsTotalCount = useSelector(itemsTotalCountInvitationsSelector);
  const isInvitationsLoading = useSelector(isLoadingInvitationsSelector);
  const invitationsPageFilter = useSelector(filterInvitationsSelector);
  const infiniteListItems = useSelector(listInvitationsSelector);
  const dispatch = useAppDispatch();

  const userInfo = useSelector(selectInfoUserSelector);

  const hasNextPage = infiniteListItems.length < invitationsTotalCount;
  const loadNextPage = async () => {
    dispatch(
      getInvitationsInfiniteScroll({
        ...invitationsPageFilter,
        skip: infiniteListItems.length,
        take: 20,
      })
    );
  };

  const itemCount = hasNextPage
    ? infiniteListItems.length + 1
    : infiniteListItems.length;
  const loadMoreItems = isInvitationsLoading ? () => { } : loadNextPage;
  const isItemLoaded = (index: number) =>
    !hasNextPage || index < infiniteListItems.length;

  const InvitationsTableItem: React.FC<ListChildComponentProps<IInvitation[]>> = (props) => {
    const content = infiniteListItems[props.index];

    if (!isItemLoaded(props.index))
      return (
        <div style={props.style} className={styles.loadNextTableIndicator}>
          <PulseLoader
            color="#0168fa"
            size={8}
            margin={5}
            speedMultiplier={1}
          />
        </div>
      );

    return (
      <div style={props.style}>
        <div className={`
          ${styles.fRow} 
          ${styles.fBodyRow} 
          ${content.member !== null ? invitationsTableStyles.bgLightGreen : ""}
          `}
        >
          <div className={`${styles.fCell} ${styles.fBodyCell}`}>
            <span className={`${styles.fBodyCellContent}`}>{content.role}</span>
          </div>

          <div className={`${styles.fCell} ${styles.fBodyCell}`}>
            <span className={`${styles.fBodyCellContent} `}>
              {format(dateToTimeZone(content.expirationDate, userInfo?.timeZone.name ?? "UTC"), 'MMM dd yyyy hh:mm a')}
            </span>
          </div>
          {invitationsPageFilter.showAccepted &&
            <div className={`${styles.fCell} ${styles.fBodyCell}`}>
              <span
                id={content.id + 'name'}
                className={`${styles.fBodyCellContent}`}
                data-tooltip-content={content.member?.name ?? ""}
              >
                {content.member?.name}
              </span>
              <ReactTooltip
                anchorId={content.id + 'name'}
                style={{ zIndex: 1 }}
              />
            </div>
          }
          <div className={`${styles.fCell} ${styles.fBodyCell}`}>
            <span className={`${styles.fBodyCellContent}`}>{content.email}</span>
          </div>
          <div className={`${styles.fCell} ${styles.fBodyCell}`}>
            {(!content.member) &&
              <ActionsPopover>
                <InvitationLinksActionsList
                  invitationId={content.id}
                  invitationLink={content.invitationLink}
                />
              </ActionsPopover>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.fTable}>
        <div className={`${styles.fRow} ${styles.fHeaderRow}`}>
          {invitationLinksTableColumns.filter(col => !(col.name === 'User name' && !invitationsPageFilter.showAccepted)).map((col) => (
            <div
              key={col.name}
              className={`${styles.fCell} ${styles.fHeaderCell}`}
            >
              {col.name}
            </div>
          ))}
        </div>
        <div className={styles.fTableBody}>
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <FixedSizeList
                    itemCount={itemCount}
                    onItemsRendered={onItemsRendered}
                    ref={ref}
                    height={height}
                    overscanCount={1}
                    itemSize={70}
                    width={width}
                  >
                    {InvitationsTableItem}
                  </FixedSizeList>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        </div>
      </div>
    </>
  );
};

export default InvitationLinksTable;
